import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, X, UserPlus, Mail, Lock, User } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const emptyForm = { id: '', email: '', name: '', password: '' };

const UsersAdmin = () => {
  const { toast } = useToast();
  const { email: currentEmail } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setOpen(true); };
  const openEdit = (u) => { setForm({ id: u.id, email: u.email, name: u.name || '', password: '' }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.id && !form.email.trim()) return toast({ title: 'El correo es obligatorio' });
    if (!form.id && !form.password) return toast({ title: 'La contraseña es obligatoria' });
    if (form.password && form.password.length < 6) return toast({ title: 'La contraseña debe tener al menos 6 caracteres' });

    setSaving(true);
    try {
      if (form.id) {
        const payload = { name: form.name };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${form.id}`, payload);
        toast({ title: 'Usuario actualizado' });
      } else {
        await api.post('/users', { email: form.email.trim(), password: form.password, name: form.name });
        toast({ title: 'Usuario creado' });
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo guardar' });
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/users/${deleting.id}`);
      toast({ title: 'Usuario eliminado' });
      setDeleting(null);
      await load();
    } catch (err) {
      toast({ title: 'No se pudo eliminar', description: err?.response?.data?.detail || '' });
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('es-PA', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch (_e) { return iso; }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Usuarios</h2>
          <p className="text-sm text-slate-500">{users.length} con acceso al panel</p>
        </div>
        <Button onClick={openNew} className="h-10 gap-2 bg-emerald-700 text-white hover:bg-emerald-800">
          <UserPlus className="h-4 w-4" /> Nuevo usuario
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-700" /></div>
      ) : users.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 py-16 text-center text-slate-500">
          No hay usuarios.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Correo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Rol</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Creado</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map(u => {
                const isMe = u.email.toLowerCase() === (currentEmail || '').toLowerCase();
                return (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                          <User className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900">{u.name || '—'}</p>
                          {isMe && <span className="text-xs text-emerald-700">(Tú)</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{u.email}</td>
                    <td className="px-4 py-3">
                      <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 capitalize">{u.role}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{fmtDate(u.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(u)} className="h-8 gap-1 border-slate-300">
                          <Pencil className="h-3.5 w-3.5" /> Editar
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setDeleting(u)}
                          disabled={isMe}
                          className="h-8 gap-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre completo"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label>Correo electrónico</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="usuario@dominio.com"
                  className="h-11 pl-10"
                  disabled={!!form.id}
                  required={!form.id}
                />
              </div>
              {form.id && <p className="text-xs text-slate-500">El correo no se puede cambiar.</p>}
            </div>
            <div className="space-y-2">
              <Label>{form.id ? 'Nueva contraseña (opcional)' : 'Contraseña'}</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={form.id ? 'Dejar vacío para no cambiar' : 'Mínimo 6 caracteres'}
                  className="h-11 pl-10"
                  required={!form.id}
                  minLength={form.id ? 0 : 6}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="gap-1"><X className="h-4 w-4" /> Cancelar</Button>
              <Button type="submit" disabled={saving} className="gap-2 bg-emerald-700 text-white hover:bg-emerald-800">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {form.id ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleting?.email}</strong> y perderá su acceso al panel. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersAdmin;
